/**
 * TestsAnalyzedCard - Displays test execution analysis results
 * 
 * Shows test counts, pass/fail status, coverage, constraint satisfaction,
 * and test infrastructure details.
 */
import { cn } from '@/lib/utils'
import { 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Check, 
  X, 
  FlaskConical,
  BarChart3,
  AlertCircle,
  Terminal
} from 'lucide-react'

export interface TestsAnalyzedData {
  total_tests?: number
  passed?: number
  failed?: number
  skipped?: number
  coverage_percent?: number | null
  failures?: Array<string | { test?: string; message?: string; stack?: string }>
  coverage_gaps?: Array<string>
  constraints_satisfied?: Record<string, boolean>
  test_infrastructure?: {
    build_successful?: boolean
    test_command?: string
    exit_code?: number
    warnings?: number
    errors?: number
  }
}

export interface TestsAnalyzedCardProps {
  data: TestsAnalyzedData
  reasoning?: string
  warnings?: Array<string>
  className?: string
}

// Helper to get failure text
function getFailureText(failure: string | { test?: string; message?: string; stack?: string }): string {
  if (typeof failure === 'string') return failure
  if (failure.test && failure.message) {
    return `${failure.test}: ${failure.message}`
  }
  return failure.message || failure.test || JSON.stringify(failure)
}

export function TestsAnalyzedCard({ data, reasoning, warnings, className }: TestsAnalyzedCardProps) {
  const {
    total_tests = 0,
    passed = 0,
    failed = 0,
    skipped = 0,
    coverage_percent,
    failures = [],
    coverage_gaps = [],
    constraints_satisfied = {},
    test_infrastructure
  } = data

  const hasTests = total_tests > 0
  const allPassed = hasTests && failed === 0
  const hasFailed = failed > 0
  const hasNoTests = total_tests === 0

  // Determine overall status
  const statusColor = hasNoTests 
    ? 'yellow' 
    : allPassed 
      ? 'green' 
      : hasFailed 
        ? 'red' 
        : 'yellow'

  const StatusIcon = hasNoTests 
    ? AlertTriangle 
    : allPassed 
      ? CheckCircle 
      : hasFailed 
        ? XCircle 
        : AlertTriangle

  return (
    <div className={cn("rounded-lg border bg-card p-4 space-y-4", className)}>
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className={cn(
          "p-2 rounded-full",
          statusColor === 'green' && "bg-green-100 dark:bg-green-900/30",
          statusColor === 'red' && "bg-red-100 dark:bg-red-900/30",
          statusColor === 'yellow' && "bg-yellow-100 dark:bg-yellow-900/30"
        )}>
          <StatusIcon className={cn(
            "h-5 w-5",
            statusColor === 'green' && "text-green-600 dark:text-green-400",
            statusColor === 'red' && "text-red-600 dark:text-red-400",
            statusColor === 'yellow' && "text-yellow-600 dark:text-yellow-400"
          )} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-base">Test Analysis</h3>
            <span className={cn(
              "px-2 py-0.5 rounded-full text-xs font-medium uppercase",
              statusColor === 'green' && "bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300",
              statusColor === 'red' && "bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300",
              statusColor === 'yellow' && "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300"
            )}>
              {hasNoTests ? 'No Tests' : allPassed ? 'All Passed' : hasFailed ? 'Failed' : 'Warning'}
            </span>
          </div>
        </div>
      </div>

      {/* Test Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-muted/50 rounded-lg p-3 text-center">
          <div className="flex items-center justify-center gap-1.5 text-muted-foreground mb-1">
            <FlaskConical className="h-4 w-4" />
            <span className="text-xs font-medium">Total</span>
          </div>
          <span className="text-xl font-bold">{total_tests}</span>
        </div>
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 text-center">
          <div className="flex items-center justify-center gap-1.5 text-green-600 dark:text-green-400 mb-1">
            <CheckCircle className="h-4 w-4" />
            <span className="text-xs font-medium">Passed</span>
          </div>
          <span className="text-xl font-bold text-green-600 dark:text-green-400">{passed}</span>
        </div>
        <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 text-center">
          <div className="flex items-center justify-center gap-1.5 text-red-600 dark:text-red-400 mb-1">
            <XCircle className="h-4 w-4" />
            <span className="text-xs font-medium">Failed</span>
          </div>
          <span className="text-xl font-bold text-red-600 dark:text-red-400">{failed}</span>
        </div>
        <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3 text-center">
          <div className="flex items-center justify-center gap-1.5 text-yellow-600 dark:text-yellow-400 mb-1">
            <AlertCircle className="h-4 w-4" />
            <span className="text-xs font-medium">Skipped</span>
          </div>
          <span className="text-xl font-bold text-yellow-600 dark:text-yellow-400">{skipped}</span>
        </div>
      </div>

      {/* Coverage */}
      {coverage_percent !== undefined && coverage_percent !== null && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
              <span>Code Coverage</span>
            </div>
            <span className={cn(
              "font-semibold",
              coverage_percent >= 80 ? "text-green-600 dark:text-green-400" :
              coverage_percent >= 60 ? "text-yellow-600 dark:text-yellow-400" :
              "text-red-600 dark:text-red-400"
            )}>
              {coverage_percent.toFixed(1)}%
            </span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div 
              className={cn(
                "h-full rounded-full transition-all",
                coverage_percent >= 80 ? "bg-green-500" :
                coverage_percent >= 60 ? "bg-yellow-500" :
                "bg-red-500"
              )}
              style={{ width: `${Math.min(100, coverage_percent)}%` }}
            />
          </div>
        </div>
      )}

      {/* Test Failures */}
      {failures.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-red-600 dark:text-red-400 flex items-center gap-2">
            <XCircle className="h-4 w-4" />
            Test Failures
          </h4>
          <ul className="space-y-1 pl-1">
            {failures.map((failure, idx) => (
              <li key={idx} className="text-sm text-red-600 dark:text-red-400 flex items-start gap-2">
                <span className="mt-1">•</span>
                <span className="font-mono text-xs">{getFailureText(failure)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Coverage Gaps */}
      {coverage_gaps.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-yellow-600 dark:text-yellow-400 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Coverage Gaps
          </h4>
          <ul className="space-y-1 pl-1">
            {coverage_gaps.map((gap, idx) => (
              <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                <span className="mt-1">•</span>
                <span>{gap}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Constraints */}
      {Object.keys(constraints_satisfied).length > 0 && (
        <div className="space-y-2 pt-2 border-t">
          <h4 className="text-xs font-medium text-muted-foreground">Constraints Verified</h4>
          <div className="space-y-1">
            {Object.entries(constraints_satisfied).map(([constraint, satisfied]) => (
              <div key={constraint} className="flex items-start gap-2 text-xs">
                {satisfied ? (
                  <Check className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                ) : (
                  <X className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                )}
                <span className={cn(
                  "font-mono",
                  satisfied ? "text-muted-foreground" : "text-red-600 dark:text-red-400"
                )}>
                  {constraint}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Test Infrastructure */}
      {test_infrastructure && (
        <div className="space-y-2 pt-2 border-t">
          <h4 className="text-xs font-medium text-muted-foreground flex items-center gap-2">
            <Terminal className="h-3 w-3" />
            Test Infrastructure
          </h4>
          <div className="bg-muted/50 rounded-lg p-3 space-y-2 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Build</span>
              <span className={cn(
                "font-medium",
                test_infrastructure.build_successful ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
              )}>
                {test_infrastructure.build_successful ? 'Successful' : 'Failed'}
              </span>
            </div>
            {test_infrastructure.exit_code !== undefined && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Exit Code</span>
                <span className={cn(
                  "font-mono",
                  test_infrastructure.exit_code === 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
                )}>
                  {test_infrastructure.exit_code}
                </span>
              </div>
            )}
            {test_infrastructure.test_command && (
              <div className="pt-1 border-t border-muted">
                <span className="text-muted-foreground block mb-1">Command</span>
                <code className="font-mono text-[10px] bg-background px-1.5 py-0.5 rounded break-all">
                  {test_infrastructure.test_command}
                </code>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Warnings */}
      {warnings && warnings.length > 0 && (
        <div className="space-y-2 pt-2 border-t">
          <h4 className="text-sm font-medium text-yellow-600 dark:text-yellow-400 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Warnings
          </h4>
          <ul className="space-y-1">
            {warnings.map((warning, idx) => (
              <li key={idx} className="text-sm text-yellow-600 dark:text-yellow-400 flex items-start gap-2">
                <span className="mt-1">•</span>
                <span>{warning}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Reasoning */}
      {reasoning && (
        <div className="pt-2 border-t">
          <p className="text-xs text-muted-foreground">{reasoning}</p>
        </div>
      )}
    </div>
  )
}
